<script setup>
import { reactive, ref } from 'vue';
import { authService } from '@/services/auth-service.js';

const step = ref('input')

const form = reactive({
  email: '',
  otp: ''
})

const loading = ref(false)
const error = ref(null)
const successMessage = ref('')

const sendCode = async () => {
  error.value = null
  successMessage.value = ''

  if (!form.email) {
    error.value = 'Please enter your email address'
    return
  }

  try {
    loading.value = true
    const { error: sendError } = await authService.sendEmailOtp(form.email)
    if (sendError) throw sendError
    successMessage.value = 'A 6-digit code has been sent to your email'
    step.value = 'otp'
  } catch (e) {
    error.value = e.message || 'Failed to send code'
  } finally {
    loading.value = false
  }
}

const verifyCode = async () => {
  if (form.otp.length !== 6) {
    error.value = 'Please enter a valid 6-digit code'
    return
  }

  try {
    loading.value = true
    error.value = null
    const { error: verifyError } = await authService.verifyEmailOtp(form.email, form.otp)
    if (verifyError) {
      error.value = verifyError.message || 'Invalid or expired code. Please try again.'
    } else {
      window.location.href = '/'
    }
  } catch (e) {
    error.value = e.message || 'Verification failed'
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  step.value = 'input'
  form.otp = ''
  error.value = null
}
</script>

<template>
  <div class="page">
    <div class="wrapper">
      <div class="card-form">

        <div class="logo">
          <div class="my-4" id="logo-image"></div>
        </div>

        <form @submit.prevent="step === 'otp' ? verifyCode() : sendCode()" class="p-4">

          <div v-if="error" class="alert alert-error">{{ error }}</div>
          <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

          <div v-if="step === 'input'">
            <h3 class="text-center mb-4">Sign in with email code</h3>

            <div class="input-group">
              <input
                  type="email"
                  v-model="form.email"
                  placeholder="Enter your email"
                  required
              >
            </div>

            <button class="btn-button" :disabled="loading">
              {{ loading ? 'Sending code...' : 'Send Code' }}
            </button>
          </div>

          <div v-else-if="step === 'otp'">
            <p class="text-center mb-4">
              We sent a 6-digit verification code to<br>
              <strong>{{ form.email }}</strong>
            </p>

            <div class="input-group">
              <input
                  type="text"
                  v-model="form.otp"
                  placeholder="000000"
                  maxlength="6"
                  inputmode="numeric"
                  required
              >
            </div>

            <button class="btn-button" :disabled="loading">
              {{ loading ? 'Verifying...' : 'Verify Code & Sign In' }}
            </button>

            <div class="text-center mt-4">
              <button type="button" @click="goBack" class="link-button">
                ← Send new code
              </button>
            </div>
          </div>

        </form>

      </div>

      <div class="footer">
        Impact Journals, LLC
      </div>
    </div>
  </div>
</template>

<style>
.text-center { text-align: center; }
.mt-4 { margin-top: 1rem; }
.mb-4 { margin-bottom: 1rem; }

.link-button {
  background: none;
  border: none;
  color: #8a0f63;
  cursor: pointer;
  text-decoration: underline;
}

.link-button:hover { color: #6d0c4e; }
</style>
